import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from 'react';
import { ChevronDown, ChevronRight, Filter, FilterX } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { SimplePagination } from '@/components/shared/SimplePagination';
import { Loading } from '@/components/shared/placeholders/Loading';
import { ErrorFallback } from '@/components/shared/placeholders/Error';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import {
  hasMcpDetectionEvidence,
  stripMcpToolResponseLocationPrefix,
  trimMcpToolLocationPreview,
} from './alert-location';
import {
  ClientTypeCell,
  ClientTypesCell,
  OutcomeBadge,
  OccurrenceOutcomeCell,
} from '@/components/DataTable/alerts/CellRenderers';
import {
  useUserAlertOccurrences,
  useOrganizationAlertOccurrences,
} from '@/hooks/api/useGroupedAlerts';
import { cn, getUserDisplayName } from '@/lib/utils';
import { UserTypeTag } from './UserTypeTag';
import { OccurrenceContentCell } from './OccurrenceContentCell';
import {
  formatAlertType,
  formatSubPolicyLabel,
} from '@kirin/api-client/helpers/alerts';
import { getAlertFileDisplayLabel } from '@kirin/api-client/contracts';
import { MiddleTruncatedPath } from '@/components/shared/MiddleTruncatedPath';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import {
  ORGANIZATION_SCOPE_LABEL,
  securityGroupLabel,
} from './security-groups';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  ALERT_OCCURRENCES_FILTERABLE_FIELDS,
  ALERT_OTHER_PATHS_LIMIT,
  type AlertOccurrence,
  type AlertOccurrencesResponse,
} from '@kirin/api-client/contracts/alerts';
import {
  FilterOperators,
  type FilterValue,
} from '@kirin/api-client/contracts/table';
import { ALERTS_PAGE_SIZE } from './constants';
import { AlertDetailContent } from './AlertDetailContent';
import { CapabilitySummary } from './CapabilitySummary';
import { UsersCell } from './UsersCell';
import { EndpointDetailsPopover } from '@/components/Inventory/EndpointDetailsPopover';
import { CopyToClipboardButton } from '@/components/shared/CopyToClipboardButton';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { shortenSessionId } from './shortenSessionId';
import { toPlatformId } from '@kirin/api-client/contracts/platform-registry';
import {
  sessionTranscriptLocation,
  sessionTranscriptLocationAlt,
} from './AlertSecretDetails.helpers';
import { SessionTranscriptHintBody } from './SessionTranscriptHint';
import { occurrenceToDetailAlert } from './occurrence-detail-alert';
import { EndpointSearchInput } from '@/components/shared/EndpointSearchInput';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

/*
  KIR-4281: in-row controls that own their own click (session filter, copy,
  endpoint popover trigger) must not also toggle the row. One guard here beats
  a `stopPropagation` per cell — it survives the next control added, and these
  components are shared with tables where that would be wrong. Radix popover
  CONTENT is portalled to `body`, so it never reaches the row anyway.
*/
const ROW_INTERACTIVE_SELECTOR =
  'button, a, input, select, textarea, [role="button"], [role="menuitem"]';

function isInteractiveTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(ROW_INTERACTIVE_SELECTOR) !== null
  );
}

const SESSION_FILTER_LABEL = 'Show only this session';
const SESSION_UNFILTER_LABEL = 'Show all sessions';

function OccurrenceSessionIdCell({
  sessionId,
  clientType,
  isFiltered,
  onFilterClick,
}: {
  sessionId?: string;
  clientType?: string | null;
  isFiltered: boolean;
  onFilterClick: (sessionId: string) => void;
}): ReactElement {
  if (!sessionId) {
    return (
      <span className="block w-full text-center text-muted-foreground">—</span>
    );
  }
  const filterLabel = isFiltered
    ? SESSION_UNFILTER_LABEL
    : SESSION_FILTER_LABEL;
  const FilterIcon = isFiltered ? FilterX : Filter;
  const platform = toPlatformId(clientType);
  const location = sessionTranscriptLocation(platform, sessionId);
  const altLocation = sessionTranscriptLocationAlt(platform, sessionId);
  return (
    <span className="inline-flex min-w-0 max-w-full items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="shrink-0 whitespace-nowrap font-mono text-xs">
            {shortenSessionId(sessionId)}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <SessionTranscriptHintBody
            firstLine={sessionId}
            firstLineClassName="font-mono break-all"
            location={location}
            altLocation={altLocation}
          />
        </TooltipContent>
      </Tooltip>
      <CopyToClipboardButton
        text={sessionId}
        variant="ghost"
        compactCopiedState
        className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground"
      >
        <span className="sr-only">Copy session ID</span>
      </CopyToClipboardButton>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            aria-pressed={isFiltered}
            aria-label={filterLabel}
            className={cn(
              'h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground',
              isFiltered && 'text-foreground'
            )}
            onClick={() => onFilterClick(sessionId)}
          >
            <FilterIcon className="h-4 w-4" aria-hidden />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{filterLabel}</TooltipContent>
      </Tooltip>
    </span>
  );
}

interface AlertOccurrencesPanelProps {
  alertKey: string;
  organizationId?: string;
  /** Column filters from the main alerts table to apply to occurrences. */
  filters?: FilterValue[];
  /** When "modal", table scrolls and pagination is fixed at bottom. */
  layout?: 'default' | 'modal';
  /** Called when data has loaded (e.g. for modal title). */
  onDataLoaded?: (data: AlertOccurrencesResponse) => void;
}

/**
 * KIR-5368: the alert's other paths — copies of the same content (a git
 * worktree, an overlay, a vendored copy) that the agent recognized without
 * scanning them again. Places, not detections: listed apart from the instances
 * and never counted with them.
 */
function OtherPathsSection({
  otherPaths,
  total,
}: {
  otherPaths: AlertOccurrence[];
  total: number;
}): ReactElement {
  return (
    <section className="min-w-0" data-testid="alert-other-paths">
      <h3 className="text-sm font-medium">
        Also found at {total} other path{total === 1 ? '' : 's'}
      </h3>
      <p className="mb-2 text-xs text-muted-foreground">
        Copies of the same content, such as a git worktree, that Kirin
        recognized without scanning them again. They are not counted as
        instances.
      </p>
      <div className="w-full min-w-0 rounded-md border">
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead className="w-[200px]">User</TableHead>
              <TableHead className="w-[140px]">Last Seen</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* The same path can sit on two machines: key on position too. */}
            {otherPaths.map((path, index) => (
              <TableRow key={`${index}-${path.filePath}`}>
                <TableCell className="text-sm max-w-[520px]">
                  <div className="max-w-[520px] overflow-hidden">
                    <MiddleTruncatedPath
                      path={path.filePath}
                      className="text-xs"
                      showInlineCopy
                    />
                  </div>
                </TableCell>
                <TableCell className="text-sm">
                  {getUserDisplayName(path.user)}
                </TableCell>
                <TableCell className="text-sm">
                  <RelativeTimeWithTooltip date={path.scannedAt} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {total > otherPaths.length && (
        <p className="pt-2 text-xs text-muted-foreground">
          Showing the {ALERT_OTHER_PATHS_LIMIT} most recently seen.
        </p>
      )}
    </section>
  );
}

/**
 * Renders paginated occurrences for a grouped alert.
 * Uses user-level API when organizationId is omitted, org-level when provided.
 * Uses shared ui/table, SimplePagination, Loading, and ErrorFallback.
 */
export function AlertOccurrencesPanel({
  alertKey,
  organizationId,
  filters,
  layout = 'default',
  onDataLoaded,
}: AlertOccurrencesPanelProps): ReactElement | null {
  const [page, setPage] = useState(1);
  const [sessionIdFilter, setSessionIdFilter] = useState<string | null>(null);
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERTS_REVAMP,
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
    flagsmithFeatureFlags.ENDPOINT_IDENTIFIER,
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
    flagsmithFeatureFlags.ALERT_OCCURRENCE_SESSION_ID,
  ]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;
  const capabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  const endpointColumnEnabled =
    flags[flagsmithFeatureFlags.ENDPOINT_IDENTIFIER] ?? false;
  const sessionIdColumnEnabled =
    flags[flagsmithFeatureFlags.ALERT_OCCURRENCE_SESSION_ID] ?? false;
  // KIR-3800 (AC #15/#16). Org scope only: the per-user dashboard view has a
  // single user, so a per-occurrence group column would repeat one value. The
  // group itself comes from the API (`occurrence.securityGroup`), which is
  // flag-gated server-side too.
  const securityGroupColumnEnabled =
    (flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false) &&
    !!organizationId;

  // KIR-5098: endpoint name / ID search, sent to the server (paginated list).
  // A single-value Contains carried from the alerts table seeds the box and is
  // sent as the box's filter — two `endpoints[contains]` would share one query
  // key and the second would overwrite the first.
  const carriedFilter = filters?.find(
    f =>
      f.field === ALERT_OCCURRENCES_FILTERABLE_FIELDS.ENDPOINTS &&
      f.operator === FilterOperators.CONTAINS &&
      f.value.length === 1
  );
  const carriedQuery = carriedFilter?.value[0]?.trim() ?? '';
  const [endpointQuery, setEndpointQuery] = useState(carriedQuery);
  const debouncedTyped = useDebouncedValue(endpointQuery.trim());
  // Empty (cleared) or the carried value: apply now, not after the debounce.
  const typedQuery = endpointQuery.trim();
  let debouncedEndpointQuery =
    !typedQuery || typedQuery === carriedQuery ? typedQuery : debouncedTyped;
  // KIR-4281: the open occurrence, or null. Single-open accordion — the panel
  // is tall and this table sits in a scrolling dialog, so two would bury it.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [sessionIdLatch, setSessionIdLatch] = useState({
    key: alertKey,
    seen: false,
  });
  if (sessionIdLatch.key !== alertKey) {
    // This pass still holds the previous alert's search — don't send it.
    debouncedEndpointQuery = '';
    setSessionIdLatch({ key: alertKey, seen: false });
    setSessionIdFilter(null);
    setEndpointQuery(carriedQuery);
    setPage(1);
    setExpandedKey(null);
  }
  const mergedFilters = useMemo((): FilterValue[] | undefined => {
    const merged = (filters ?? []).filter(
      f =>
        f.field !== ALERT_OCCURRENCES_FILTERABLE_FIELDS.SESSION_ID &&
        f !== carriedFilter &&
        // No Endpoint column (flag off) → no endpoint filtering either; a
        // stale URL must not filter by a value the user can't see or clear.
        (endpointColumnEnabled ||
          f.field !== ALERT_OCCURRENCES_FILTERABLE_FIELDS.ENDPOINTS)
    );
    if (sessionIdFilter) {
      merged.push({
        field: ALERT_OCCURRENCES_FILTERABLE_FIELDS.SESSION_ID,
        operator: FilterOperators.INCLUDES,
        value: [sessionIdFilter],
      });
    }
    // ANDs with any other carried endpoint filter (Equals / Excludes).
    if (debouncedEndpointQuery && endpointColumnEnabled) {
      merged.push({
        field: ALERT_OCCURRENCES_FILTERABLE_FIELDS.ENDPOINTS,
        operator: FilterOperators.CONTAINS,
        value: [debouncedEndpointQuery],
      });
    }
    return merged.length > 0 ? merged : undefined;
  }, [
    filters,
    carriedFilter,
    sessionIdFilter,
    debouncedEndpointQuery,
    endpointColumnEnabled,
  ]);
  const params = {
    page,
    limit: ALERTS_PAGE_SIZE,
    ...(mergedFilters && mergedFilters.length > 0
      ? { filters: mergedFilters }
      : {}),
  };

  const userQuery = useUserAlertOccurrences(
    organizationId ? undefined : alertKey,
    params,
    !organizationId
  );
  const orgQuery = useOrganizationAlertOccurrences(
    organizationId ?? undefined,
    organizationId ? alertKey : undefined,
    params,
    !!organizationId
  );

  const {
    data: fetchedData,
    isLoading: isFetchingFirst,
    isError,
    refetch,
  } = organizationId ? orgQuery : userQuery;
  // KIR-5098: while a new endpoint search loads, keep showing the last result
  // for the same alert, page and session filter, so the panel (and the search
  // box) doesn't flash to a spinner. Any other change still shows loading.
  const viewKey = `${alertKey}|${page}|${sessionIdFilter ?? ''}`;
  const [lastData, setLastData] = useState<{
    key: string;
    data: AlertOccurrencesResponse;
  } | null>(null);
  if (fetchedData && lastData?.data !== fetchedData) {
    setLastData({ key: viewKey, data: fetchedData });
  }
  const data =
    fetchedData ?? (lastData?.key === viewKey ? lastData.data : undefined);
  const isLoading = isFetchingFirst && !data;

  const sessionIdInPayload = Boolean(
    data?.alert.sessionId || data?.occurrences.some(occ => occ.sessionId)
  );
  if (
    sessionIdLatch.key === alertKey &&
    sessionIdInPayload &&
    !sessionIdLatch.seen
  ) {
    setSessionIdLatch({ key: alertKey, seen: true });
  }

  useEffect(() => {
    if (data) onDataLoaded?.(data);
  }, [data, onDataLoaded]);

  if (isLoading) {
    return <Loading label="Loading instances..." className="min-h-0 py-8" />;
  }

  if (isError) {
    return (
      <ErrorFallback
        retry={() => refetch()}
        label="Failed to load the alert's instances."
      />
    );
  }

  const hasOccurrences = data && data.occurrences.length > 0;

  if (!data) {
    return null;
  }

  const alert = data.alert;
  // KIR-4282: independent column flag AND payload presence. Do not reuse
  // `secret_detection_details` here — that flag already drives the parent
  // More-details Session ID, and a flag-on / payload-off column of dashes is
  // a confident wrong answer. Parent `sessionId` (latest) covers the usual
  // case; the latch keeps the column from vanishing when a later page's rows
  // lack IDs. First paint of an alert that has never shown an ID still hides
  // the column until a row (or the parent) carries one.
  const showSessionIdColumn =
    sessionIdColumnEnabled &&
    (sessionIdInPayload ||
      (sessionIdLatch.key === alertKey && sessionIdLatch.seen));

  /*
    KIR-4281: the detail row spans the table, so colSpan must track the three
    flag-conditional columns. Fixed count, in render order: chevron, #, User,
    Last Seen, File, Content, Agent Platform, Severity, Outcome, Score, Title.
    The test asserts this against the real header count for every flag
    combination — a stale integer here misaligns the table silently.
  */
  const OCCURRENCE_FIXED_COLUMN_COUNT = 11;
  const occurrenceColumnCount =
    OCCURRENCE_FIXED_COLUMN_COUNT +
    (securityGroupColumnEnabled ? 1 : 0) +
    (endpointColumnEnabled ? 1 : 0) +
    (showSessionIdColumn ? 1 : 0);

  function handleSessionIdClick(nextSessionId: string): void {
    setSessionIdFilter(current =>
      current === nextSessionId ? null : nextSessionId
    );
    setPage(1);
    // The row set changes under the open key — close it.
    setExpandedKey(null);
  }

  function toggleExpanded(key: string): void {
    setExpandedKey(current => (current === key ? null : key));
  }

  const visibleOccurrences =
    sessionIdFilter === null
      ? data.occurrences
      : data.occurrences.filter(occ => occ.sessionId === sessionIdFilter);

  // When filters are active, the first instance in the breakdown table may
  // not be the actual "Latest" (most recent) one. Show "1" instead of
  // "Latest" when the original latest was filtered out.
  // Compare as epoch ms to avoid ISO-8601 formatting differences (Z vs +00:00).
  // KIR-4682: compare with baseScannedAt; lastSeenAt can be a later worktree copy.
  const isBaseFilteredOut =
    data.totalUnfiltered !== undefined &&
    data.totalUnfiltered > data.pagination.total &&
    hasOccurrences &&
    new Date(data.occurrences[0].scannedAt).getTime() !==
      new Date(data.baseScannedAt ?? alert.lastSeenAt).getTime();

  const tableContent = hasOccurrences ? (
    <div
      className="w-full min-w-0 rounded-md border"
      data-testid="alert-occurrences-table"
    >
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            {/* Chevron column. Unlabelled: the tests discriminate this table by
                its "#" header, and "Latest" + a chevron will not fit that cell. */}
            <TableHead className="w-8" />
            <TableHead className="w-12">#</TableHead>
            <TableHead>User</TableHead>
            {securityGroupColumnEnabled && (
              <TableHead className="w-[140px]">Security Group</TableHead>
            )}
            <TableHead className="w-[100px]">Last Seen</TableHead>
            <TableHead className="max-w-[520px]">File</TableHead>
            {/* min-width floors the Content column so the auto-layout table can't
                crush it to ~1ch (its inner code block uses break-all, giving a
                1-character min-content width). Floor is honored on a native td;
                the table's own overflow-x wrapper scrolls if the row overflows. */}
            <TableHead className="w-[220px] min-w-[200px]">Content</TableHead>
            <TableHead className="w-[140px]">Agent Platform</TableHead>
            {endpointColumnEnabled && (
              <TableHead className="w-[140px]">Endpoint</TableHead>
            )}
            {showSessionIdColumn && (
              <TableHead className="w-[160px]">Session ID</TableHead>
            )}
            <TableHead className="w-[80px]">Severity</TableHead>
            <TableHead className="w-[100px]">Outcome</TableHead>
            <TableHead className="w-14">Score</TableHead>
            <TableHead className="w-[220px]">
              {revampEnabled ? 'Title' : 'Type'}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visibleOccurrences.map((occ, idx) => {
            const globalIndex = (page - 1) * ALERTS_PAGE_SIZE + idx;
            const indexLabel =
              globalIndex === 0 && !isBaseFilteredOut
                ? 'Latest'
                : globalIndex + 1;
            /*
              KIR-4281: `occ.id` (findings.id) is the only unique handle —
              `scanId` is shared by every finding of one scan. The fallback
              (reference-only occurrences, older payloads) uses the GLOBAL
              index so it stays distinct across pages.
            */
            const occurrenceKey = occ.id ?? `${occ.scanId}-${globalIndex}`;
            const isExpanded = expandedKey === occurrenceKey;
            const detailId = `occurrence-detail-${occurrenceKey}`;
            return (
              <Fragment key={occurrenceKey}>
                <TableRow
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={event => {
                    if (isInteractiveTarget(event.target)) return;
                    toggleExpanded(occurrenceKey);
                  }}
                >
                  {/* The chevron carries the accessible expand/collapse
                      contract; the row onClick is a mouse convenience. A
                      role="button" on the <tr> would destroy its role="row". */}
                  <TableCell className="w-8">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      aria-expanded={isExpanded}
                      aria-controls={detailId}
                      aria-label={
                        isExpanded
                          ? `Hide details for ${indexLabel}`
                          : `Show details for ${indexLabel}`
                      }
                      onClick={() => toggleExpanded(occurrenceKey)}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {indexLabel}
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="inline-flex items-center gap-1.5">
                      {getUserDisplayName(occ.user)}
                      <UserTypeTag userType={occ.user.userType} />
                    </span>
                  </TableCell>
                  {securityGroupColumnEnabled && (
                    // One occurrence is one event by one user, so the API returns
                    // exactly one group — or `null` when it was produced outside
                    // any group, which reads as the organization scope.
                    //
                    // `undefined` is a THIRD state and must not collapse into
                    // "Organization": it means the field was not returned at all,
                    // which happens when the frontend flag is on but the backend
                    // one is not (same Flagsmith key, resolved through different
                    // paths, so they can disagree mid-rollout). Asserting
                    // "ungrouped" there would be a confident wrong answer, so show
                    // a dash — mirroring AlertSecurityGroupsCell.
                    <TableCell className="text-sm">
                      {occ.securityGroup === undefined ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <Badge variant="outline">
                          {occ.securityGroup
                            ? securityGroupLabel(occ.securityGroup)
                            : ORGANIZATION_SCOPE_LABEL}
                        </Badge>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="text-sm">
                    <RelativeTimeWithTooltip date={occ.scannedAt} />
                  </TableCell>
                  <TableCell className="text-sm max-w-[520px]">
                    {/* Block wrapper clamps the column's max-content contribution
                      to 520px so the width cap holds in this native
                      auto-layout table (max-width on a <td> alone is not
                      reliably honored). MiddleTruncatedPath stays w-full, so it
                      still shrinks below 520px on narrow viewports. */}
                    <div className="max-w-[520px] overflow-hidden">
                      <MiddleTruncatedPath
                        path={getAlertFileDisplayLabel({
                          filePath: occ.filePath,
                          location: occ.location,
                          eventKind: occ.eventKind,
                        })}
                        className="text-xs"
                        showInlineCopy
                      />
                    </div>
                  </TableCell>
                  <TableCell className="text-sm w-[220px] min-w-[200px]">
                    {occ.location ? (
                      /* KIR-4175: drop the head-truncated description preview from
                        the location when this occurrence's evidence is shown in
                        "More details". KIR-4100: strip the `mcp-tool-response:`
                        namespace. Both display-only, mirror AlertDetailCard.
                        KIR-4749: the cell caps long content at ~3 lines. */
                      <OccurrenceContentCell
                        location={
                          stripMcpToolResponseLocationPrefix(
                            hasMcpDetectionEvidence(occ.sourceDetails)
                              ? (trimMcpToolLocationPreview(occ.location) ??
                                  occ.location)
                              : occ.location
                          ) ?? occ.location
                        }
                      />
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    <ClientTypeCell value={occ.clientType} />
                  </TableCell>
                  {endpointColumnEnabled && (
                    <TableCell className="text-sm truncate max-w-[140px]">
                      {occ.endpointId ? (
                        <EndpointDetailsPopover
                          organizationId={organizationId}
                          // Endpoints are keyed on the internal users.id; occ.user.id
                          // is the Descope id after toResolvedUser (internalId holds
                          // the internal UUID). Fall back to id when unresolved.
                          userId={occ.user.internalId ?? occ.user.id}
                          machineId={occ.endpointId}
                          endpointName={occ.endpointName}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                  )}
                  {showSessionIdColumn && (
                    <TableCell className="text-sm max-w-[160px]">
                      <OccurrenceSessionIdCell
                        sessionId={occ.sessionId}
                        clientType={occ.clientType}
                        isFiltered={
                          sessionIdFilter !== null &&
                          occ.sessionId === sessionIdFilter
                        }
                        onFilterClick={handleSessionIdClick}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <SeverityBadge riskLevel={occ.severity} showIcon />
                  </TableCell>
                  <TableCell>
                    <OccurrenceOutcomeCell row={occ} />
                  </TableCell>
                  <TableCell className="text-sm">{occ.score ?? '—'}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {revampEnabled
                      ? formatSubPolicyLabel(occ.subPolicyKey, occ.type)
                      : formatAlertType(occ.type)}
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell
                      id={detailId}
                      colSpan={occurrenceColumnCount}
                      className="bg-muted/30 p-4 whitespace-normal"
                    >
                      <div
                        className="min-w-0 max-w-full"
                        data-testid="occurrence-detail-panel"
                      >
                        {/* So the Latest row's panel is not mistaken for the
                            group panel above the table. */}
                        <h3 className="mb-2 text-sm font-medium">
                          {globalIndex === 0 && !isBaseFilteredOut
                            ? 'Latest instance'
                            : `Instance ${globalIndex + 1}`}
                        </h3>
                        {/* No `scope`/`alertKey`: they surface "Add to
                            Allow/Block List", which acts on the whole group.
                            `variant` keeps only Session timeline in the
                            actions bar. */}
                        <AlertDetailContent
                          alert={occurrenceToDetailAlert(occ, alert)}
                          organizationId={organizationId}
                          variant="occurrence"
                          endpointLabel={
                            occ.endpointName ?? occ.endpointId ?? null
                          }
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  ) : (
    <div className="w-full min-w-0 py-4 text-center text-muted-foreground text-sm rounded-md border">
      {debouncedEndpointQuery
        ? 'No instances on a matching endpoint.'
        : 'No instances found.'}
    </div>
  );

  // Only where the Endpoint column shows (names are flag-gated).
  const endpointSearch = endpointColumnEnabled ? (
    <EndpointSearchInput
      value={endpointQuery}
      onChange={value => {
        setEndpointQuery(value);
        setPage(1);
        setExpandedKey(null);
      }}
      className="mb-2"
    />
  ) : null;

  const otherPathsSection =
    data.otherPaths && data.otherPaths.length > 0 ? (
      <OtherPathsSection
        otherPaths={data.otherPaths}
        total={data.alert.otherPathCount ?? data.otherPaths.length}
      />
    ) : null;

  const pagination = (
    <SimplePagination
      page={page}
      limit={ALERTS_PAGE_SIZE}
      total={data.pagination.total}
      hasMore={data.pagination.hasMore}
      onPrevious={() => {
        setPage(p => Math.max(1, p - 1));
        // A new page replaces every row; on the positional fallback an open
        // key could otherwise reopen against a different occurrence.
        setExpandedKey(null);
      }}
      onNext={() => {
        setPage(p => p + 1);
        setExpandedKey(null);
      }}
    />
  );

  if (layout === 'modal') {
    const showUserCount = !!organizationId;
    return (
      <div className="flex min-w-0 flex-col gap-4">
        {/* 1. Alert summary row — column order matches main Alerts table. Kept
            with the grouped-cards panel on too: it carries First Seen and File,
            which the panel's facts strip omits (KIR-4481 review / Bugbot). */}
        <div className="w-full min-w-0 rounded-md border bg-muted/20 overflow-hidden shrink-0">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Last Seen</TableHead>
                {showUserCount && (
                  <TableHead className="w-[80px] text-center">Users</TableHead>
                )}
                <TableHead className="w-[220px]">
                  {revampEnabled ? 'Title' : 'Type'}
                </TableHead>
                <TableHead className="w-[100px]">Severity</TableHead>
                <TableHead className="w-[100px]">Outcome</TableHead>
                <TableHead className="max-w-[520px]">File</TableHead>
                <TableHead className="w-[160px]">Agent Platform</TableHead>
                <TableHead className="w-[80px] text-center">
                  Instances
                </TableHead>
                <TableHead className="w-14">Score</TableHead>
                <TableHead className="w-[100px]">First Seen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>
                  <RelativeTimeWithTooltip date={alert.lastSeenAt} />
                </TableCell>
                {showUserCount && (
                  <TableCell className="text-center text-sm">
                    <UsersCell alert={alert} />
                  </TableCell>
                )}
                <TableCell>
                  <Badge variant="outline">
                    {revampEnabled
                      ? formatSubPolicyLabel(alert.subPolicyKey, alert.type)
                      : formatAlertType(alert.type)}
                  </Badge>
                </TableCell>
                <TableCell>
                  <SeverityBadge riskLevel={alert.severity} showIcon />
                </TableCell>
                <TableCell>
                  <OutcomeBadge outcome={alert.outcome} />
                </TableCell>
                <TableCell className="max-w-[520px] text-sm">
                  {/* See the occurrence-row File cell: block wrapper clamps the
                      column's max-content contribution so the 520px cap holds
                      in this native auto-layout table. */}
                  <div className="max-w-[520px] overflow-hidden">
                    <MiddleTruncatedPath
                      path={getAlertFileDisplayLabel({
                        filePath: alert.filePath,
                        location: alert.location,
                        eventKind: alert.eventKind,
                      })}
                      className="text-xs"
                      showInlineCopy
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <ClientTypesCell row={alert} />
                </TableCell>
                <TableCell className="text-center text-sm">
                  {alert.occurrenceCount ?? '—'}
                </TableCell>
                <TableCell className="text-sm">{alert.score ?? '—'}</TableCell>
                <TableCell>
                  <RelativeTimeWithTooltip date={alert.firstSeenAt} />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* 2. Finding detail */}
        <section className="w-full min-w-0 shrink-0">
          {capabilityDetectionEnabled && (
            <h2 className="mb-2 text-sm font-medium">Findings</h2>
          )}
          <AlertDetailContent
            alert={alert}
            className="w-full min-w-0"
            organizationId={organizationId}
            scope={
              organizationId
                ? { kind: 'organization', organizationId }
                : { kind: 'account' }
            }
            alertKey={alertKey}
          />
        </section>

        {/* 2b. Capability summary (extension-security scans only) */}
        {capabilityDetectionEnabled &&
          data.capabilities &&
          data.capabilities.length > 0 && (
            <section>
              <h2 className="mb-2 text-sm font-medium">Capabilities</h2>
              <CapabilitySummary capabilities={data.capabilities} />
            </section>
          )}

        {/* 3. Instances table + pagination */}
        <div className="min-w-0">
          {endpointSearch}
          {tableContent}
          <div className="pt-2">{pagination}</div>
        </div>

        {/* 4. Other paths (KIR-5368), apart from the instances */}
        {otherPathsSection}
      </div>
    );
  }

  if (!hasOccurrences && !debouncedEndpointQuery) {
    return (
      <div className="py-4 text-center text-muted-foreground text-sm">
        No instances found.
      </div>
    );
  }

  return (
    <div className="mt-2">
      {endpointSearch}
      {tableContent}
      {capabilityDetectionEnabled &&
        data.capabilities &&
        data.capabilities.length > 0 && (
          <CapabilitySummary capabilities={data.capabilities} />
        )}
      {pagination}
      {otherPathsSection}
    </div>
  );
}
